import React from 'react';
import styled, { css, ThemeContext, ThemeProvider } from 'styled-components';
import { white } from '@prezi/common-design';

import { BentoTheme, Color } from '../../themes/themes';
import { alpha } from '../../utils';

export type ButtonTheme = 'default' | 'primary' | 'secondary' | 'danger' | 'transparent';
type ButtonSize = 'small' | 'medium' | 'large';

type ButtonColorState = {
    text?: Color;
    color?: Color;
};

export type ButtonThemeDefinition = ButtonColorState & {
    bento: BentoTheme;
    text: string;
    color: string;
    hover?: ButtonColorState;
    outline?: ButtonColorState & {
        hover?: ButtonColorState;
    };
};

const fontSizeMap = {
    small: 12,
    medium: 14,
    large: 16,
};

const sizeMap = {
    small: 24,
    medium: 32,
    large: 38,
};

const getButtonTheme = (bento: BentoTheme, theme: ButtonTheme): ButtonThemeDefinition => {
    const themeMap = {
        default: {
            bento,
            text: bento.colors.text.main,
            color: bento.colors.neutral.pale || bento.colors.neutral.weak,
            hover: {
                text: bento.colors.text.main,
                color: bento.colors.neutral.weak,
            },
            outline: {
                text: bento.colors.text.main,
                color: bento.colors.neutral.weak,
                hover: {
                    text: bento.colors.text.main,
                    color: bento.colors.neutral.main,
                },
            },
        },
        transparent: {
            bento,
            text: bento.colors.text.main,
            color: bento.colors.transparent,
            hover: {
                text: bento.colors.text.main,
                color: bento.colors.neutral.pale || bento.colors.transparent,
            },
            outline: {
                text: bento.colors.text.main,
                hover: {
                    text: bento.colors.text.main,
                },
            },
        },
        primary: {
            bento,
            text: white,
            color: bento.colors.primary.main,
            hover: {
                text: bento.colors.text.contrast,
                color: bento.colors.primary.strong,
            },
        },
        secondary: {
            bento,
            text: white,
            color: bento.colors.secondary.main,
            hover: {
                text: bento.colors.text.contrast,
                color: bento.colors.secondary.strong,
            },
        },
        danger: {
            bento,
            text: white,
            color: bento.colors.danger.main,
            hover: {
                text: bento.colors.text.contrast,
                color: bento.colors.danger.strong,
            },
        },
    };

    return themeMap[theme];
};

const getInactiveStateCss = ({ outline, theme }: ThemedProps) => css`
    color: ${outline ? theme.outline?.text || theme.color : theme.text};
    background-color: ${outline ? theme.bento.colors.transparent : theme.color};
    border-color: ${outline ? theme.outline?.color || theme.color : theme.color};
`;

const getActiveStateCss = ({ outline, theme }: ThemedProps) => css`
    color: ${outline
        ? theme.outline?.hover?.text || theme.hover?.color || theme.color
        : theme.hover?.text || theme.text};
    background-color: ${outline
        ? alpha(
              theme.outline?.hover?.color || theme.hover?.color || theme.color || theme.bento.colors.neutral.weak,
              0.2,
          )
        : theme.hover?.color || theme.color};
    border-color: ${(outline && theme.outline?.hover?.color) ||
    theme.hover?.color ||
    theme.outline?.color ||
    theme.color};
`;

export const ButtonComponent = styled.button<ThemedProps>`
    font-family: ${({ theme }) => theme.bento.font.primary.family};
    font-weight: ${({ theme }) => theme.bento.font.primary.weight.semibold};
    font-size: ${({ size = 'medium' }) => fontSizeMap[size]}px;
    line-height: 24px;

    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    position: relative;
    cursor: pointer;
    outline: none;
    transition: background-color 0.15s, border-color 0.15s, color 0.15s;

    height: ${({ size }) => `${sizeMap[size!]}px`};
    ${({ fullWidth }) => fullWidth && 'width: 100%;'}
    ${({ size, circle }) => circle && `width: ${sizeMap[size!]}px;`}

    padding: 3px 16px;
    border: 1px solid;
    ${({ theme, circle }) =>
        circle ? 'border-radius: 50%;' : theme.bento.radius ? `border-radius: ${theme.bento.radius}px` : ''};

    ${props => (props.active ? getActiveStateCss(props) : getInactiveStateCss(props))}
    &:hover {
        ${props => getActiveStateCss(props)}
    }

    &:focus {
        border: 1px solid ${({ theme }) => theme.bento.colors.primary.main};
        box-shadow: 0 0 0 2px ${({ theme }) => theme.bento.colors.primary.pale};
    }

    &:disabled {
        cursor: initial;
        pointer-events: none;
        opacity: 0.4;
    }

    svg {
        &:first-child {
            margin-inline-start: -8px;
            margin-inline-end: 4px;
        }

        &:last-child {
            margin-inline-end: -8px;
            margin-inline-start: 4px;
        }

        &:only-child {
            margin-inline-end: -16px;
            margin-inline-start: -16px;
        }
        ${({ outline, theme, defaultIconColor }) =>
            !defaultIconColor &&
            `
        path,
        rect,
        circle {
            fill: ${outline ? theme.outline?.text || theme.color : theme.text};
        }`}
    }
`;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonTheme | ButtonThemeDefinition;
    outline?: boolean;
    size?: ButtonSize;
    fullWidth?: boolean;
    active?: boolean;
    circle?: boolean;
    defaultIconColor?: boolean;
}

type ThemedProps = ButtonProps & {
    theme: ButtonThemeDefinition;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ variant = 'default', size = 'medium', ...rest }, ref) => {
        const { bento } = React.useContext(ThemeContext);
        const buttonTheme = typeof variant === 'string' ? getButtonTheme(bento, variant) : variant;
        return (
            <ThemeProvider theme={buttonTheme}>
                <ButtonComponent ref={ref} size={size} {...rest} />
            </ThemeProvider>
        );
    },
);
